import { Modal } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FormikDatePicker, TooloButton } from 'shared/components';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { routeConstants } from 'shared/constants';
import { userLogout } from 'store/actions';
const { HOME } = routeConstants;

const validationSchema = Yup.object({
  children: Yup.array()
    .of(
      Yup.object({
        _id: Yup.string().required('Child id is required'),
        fullName: Yup.string()
          .trim()
          .required('Child name is required')
          .min(2, 'Child name must be at least 2 characters')
          .max(100, 'Child name must not exceed 100 characters'),
        dateOfBirth: Yup.date()
          .required('Required')
          .max(new Date(), 'Please enter a valid date'),
      })
    )
    .min(1, 'At least one child is required')
    .required('At least one child is required'),
});

const formatDateForInput = (value) => {
  if (!value) {
    return new Date(new Date().setFullYear(new Date().getFullYear() - 1));
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return new Date(new Date().setFullYear(new Date().getFullYear() - 1));
  }

  return date;
};

const formatChildrenForForm = (children = []) => {
  return children.map((child) => ({
    _id: child?._id || '',
    fullName: child?.fullName || '',
    dateOfBirth: formatDateForInput(child?.dateOfBirth),
  }));
};

const MissingChildDobModal = ({
  show,
  children = [],
  loading,
  onSubmit,
  onProfileUpdate,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [logoutLoading, setLogoutLoading] = useState(false);
  const modalChildren = formatChildrenForForm(children);
  const modalKey = children.map((child) => child?._id).join('-');

  useEffect(() => {
    if (!show) {
      setLogoutLoading(false);
    }
  }, [show]);

  const handleLogoutClicked = (e) => {
    if (e) {
      e.preventDefault();
    }
    setLogoutLoading(true);
    requestAnimationFrame(() => {
      dispatch(userLogout());
      history.push(HOME.route);
    });
  };

  return (
    <Modal
      show={show}
      className="missing-child-dob-modal"
      dialogClassName="missing-child-dob-dialog"
      centered
      backdrop="static"
      keyboard={false}
      animation={false}
      autoFocus={false}
      enforceFocus={false}
    >
      <Modal.Header>
        <Modal.Title>Update Child Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2 description__childinfo">
          Please review and update the child details below to continue.
        </p>
        <Formik
          key={modalKey || 'missing-child-dob'}
          initialValues={{
            children: modalChildren,
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            const submitPayload = {
              children: values.children.map((child) => ({
                _id: child._id,
                fullName: child.fullName.trim(),
                dateOfBirth: Array.isArray(child.dateOfBirth)
                  ? child.dateOfBirth[0]
                  : child.dateOfBirth,
              })),
            };

            try {
              const success = await onSubmit(submitPayload);
              if (success) {
                toast.success('Child information updated.');
              }
            } catch (error) {
              toast.error(
                error?.response?.data?.message ||
                  'Unable to update child information. Please try again.'
              );
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div className="missing-child-modal-scroll">
                {values.children.map((child, index) => (
                  <div
                    className="missing-child-dob-card"
                    key={child._id || `child-${index}`}
                  >
                    <p className="missing-child-dob-card-title childInformationHeading">
                      Update details for{' '}
                      <strong>{child.fullName || `Child ${index + 1}`}</strong>
                    </p>
                    <div className="row missing-child-grid">
                      <div className="col-12 col-md-6">
                        <div className="dash-fldrw mb-0">
                          <label className="labelfrmtxt smallone">
                            Child Name<small className="required">*</small>
                          </label>
                          <div className="innerfldsdsh">
                            <Field
                              type="text"
                              name={`children[${index}].fullName`}
                              className="form-control"
                              placeholder="Child Name"
                              aria-label={`Child ${index + 1} name`}
                            />
                            {child.fullName && (
                              <button
                                type="button"
                                className="clear-name-btn"
                                onClick={() =>
                                  setFieldValue(
                                    `children[${index}].fullName`,
                                    ''
                                  )
                                }
                                aria-label="Clear name"
                              >
                                &times;
                              </button>
                            )}
                            <span>
                              <img
                                src={
                                  require('assets/images/icons/user.png')
                                    .default
                                }
                                alt="name"
                              />
                            </span>
                          </div>
                          <ErrorMessage
                            name={`children[${index}].fullName`}
                            component="div"
                            className="errornotif"
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="dash-fldrw mb-0">
                          <label className="labelfrmtxt smallone">
                            Date of Birth<small className="required">*</small>
                          </label>
                          <div className="innerfldsdsh new-datepicker">
                            <FormikDatePicker
                              key={
                                child._id || `missing-child-dob-picker-${index}`
                              }
                              type="date"
                              name={`children[${index}].dateOfBirth`}
                              placeholder="Date of Birth"
                              aria-label={`Child ${index + 1} date of birth`}
                              minDate={
                                new Date(
                                  new Date().setFullYear(
                                    new Date().getFullYear() - 20
                                  )
                                )
                              }
                              maxDate={new Date()}
                            />
                            <span style={{ pointerEvents: 'none' }}>
                              <img
                                src={
                                  require('assets/images/icons/calender.png')
                                    .default
                                }
                                alt="calendar"
                              />
                            </span>
                          </div>
                          <ErrorMessage
                            name={`children[${index}].dateOfBirth`}
                            component="div"
                            className="errornotif"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <style>{`
                .new-datepicker .flatpickr-calendar.open {
                  z-index: 11000;
                }
                .missing-child-dob-dialog {
                  max-width: 900px;
                }
                .missing-child-dob-modal .modal-content {
                  max-height: 90vh;
                  overflow: hidden;
                }
                .missing-child-dob-modal .modal-header,
                .missing-child-dob-modal .modal-body {
                  padding: 12px 16px;
                }
                .missing-child-modal-scroll {
                  max-height: calc(90vh - 170px);
                  overflow-y: auto;
                  padding-right: 4px;
                }
                .missing-child-dob-card {
                  border: 1px solid #ece4dc;
                  border-radius: 12px;
                  padding: 10px 12px;
                  margin-bottom: 8px;
                  background: #fffaf6;
                }
                .missing-child-dob-card-title {
                  margin: 0 0 4px;
                  line-height: 1.2;
                  color: #4b4b4b;
                }
                .missing-child-dob-modal .childInformationHeading {
                  padding: 0 !important;
                }
                .missing-child-grid {
                  row-gap: 2px;
                }
                .missing-child-dob-modal .dash-fldrw .labelfrmtxt {
                  margin-bottom: 2px;
                }
                .clear-name-btn {
                  position: absolute;
                  right: 36px;
                  top: 50%;
                  transform: translateY(-50%);
                  background: none;
                  border: none;
                  font-size: 20px;
                  color: #999;
                  cursor: pointer;
                  padding: 0 4px;
                  line-height: 1;
                  z-index: 2;
                }
                .clear-name-btn:hover {
                  color: #333;
                }
                .missing-child-dob-modal .dash-fldrw,
                .missing-child-dob-modal .innerfldsdsh {
                  margin-bottom: 0;
                }
                .missing-child-dob-modal .errornotif {
                  margin-top: 2px;
                  margin-bottom: 0;
                  font-size: 12px;
                  line-height: 1.2;
                }
                .modal {
                  animation: none !important;
                  transition: none !important;
                }
                .modal.fade {
                  opacity: 1 !important;
                  transition: none !important;
                }
                .modal-backdrop {
                  animation: none !important;
                  transition: none !important;
                }
                .modal-backdrop.fade {
                  opacity: 0.5 !important;
                  transition: none !important;
                }
                .missing-child-dob-modal.modal-static .modal-dialog {
                  transform: none !important;
                  transition: none !important;
                }
                .modal-dialog.missing-child-dob-dialog .modal-content{
                  border:1px solid transparent;}
                  .logout__btn{
                    font-size: 15px;
                    color: #454545;
                    cursor: pointer;
                    margin-left: 16px;
                    font-weight: 500;
                  }
                  .logout__btn:hover{
                    text-decoration: underline;
                  }
                  .logout__btn.disabled {
                    color: #9a9a9a;
                    cursor: not-allowed;
                    pointer-events: none;
                    text-decoration: none;
                  }

                  .logoutalign{
                    display:flex;
                    align-items:center;                   
                  }
                @media (max-width: 767px) {
                  .missing-child-dob-modal .modal-header,
                  .missing-child-dob-modal .modal-body {
                    padding: 12px;
                  }
                  .missing-child-modal-scroll {
                    max-height: calc(90vh - 165px);
                  }
                }
              `}</style>
              <div className="dash-submitbx mb-0 logoutalign">
                <TooloButton
                  buttonText="Update Children Information"
                  loadingText="Saving ..."
                  loading={loading}
                  type="submit"
                  className="dash-submit hvr-outline-out smallbtn"
                />
                <span
                  className={`logout__btn ${logoutLoading ? 'disabled' : ''}`}
                  onClick={handleLogoutClicked}
                  role="button"
                  tabIndex={0}
                >
                  {logoutLoading ? 'Logging out...' : 'Logout'}
                </span>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default MissingChildDobModal;
